/**
 * Modal infrastructure.
 *
 * Mounts into the `#modalRoot` div from index.html. Backdrop click is
 * intentionally a no-op — the user must use ✕, Cancel, or Save. We track a
 * "dirty" flag the moment any input/select/textarea in the modal is touched,
 * so navigation can warn before discarding work.
 */
import { $, $$, escapeHtml } from "../lib/dom";

let _modalDirty = false;

export function markModalDirty(): void { _modalDirty = true; }
export function isModalOpen(): boolean {
  return ($("#modalRoot")?.children.length ?? 0) > 0;
}
export function isModalDirty(): boolean { return isModalOpen() && _modalDirty; }

export interface ModalOptions {
  title: string;
  body: string;     // HTML string
  footer?: string;  // HTML string
  wide?: boolean;
}

export function openModal({ title, body, footer, wide }: ModalOptions): void {
  const root = $("#modalRoot");
  if (!root) return;
  root.innerHTML = `
    <div class="fixed inset-0 z-40 modal-backdrop flex items-start justify-center p-3 sm:p-6 overflow-y-auto">
      <div class="panel w-full ${wide ? "max-w-4xl" : "max-w-xl"} my-6 shadow-2xl">
        <div class="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <h3 class="font-semibold">${escapeHtml(title)}</h3>
          <button class="btn btn-ghost !py-1 !px-2" id="_modalClose" title="Close">✕</button>
        </div>
        <div class="p-4">${body}</div>
        ${footer ? `<div class="p-4 border-t border-[var(--border)] flex flex-wrap justify-end gap-2 sticky bottom-0 z-20 bg-[var(--panel)] shadow-[0_-10px_15px_-10px_rgba(0,0,0,0.6)]">${footer}</div>` : ""}
      </div>
    </div>`;
  _modalDirty = false;
  document.getElementById("_modalClose")?.addEventListener("click", closeModal);
  // Track dirtiness so navigation can warn about unsaved changes.
  $$("input, select, textarea", root).forEach((el) => {
    el.addEventListener("input", markModalDirty);
    el.addEventListener("change", markModalDirty);
  });
}

export function closeModal(): void {
  const root = $("#modalRoot");
  if (root) root.innerHTML = "";
  _modalDirty = false;
}

// Expose for inline onclick handlers in modal footer HTML (used heavily in chunk 5 forms).
(window as unknown as Record<string, unknown>).openModal = openModal;
(window as unknown as Record<string, unknown>).closeModal = closeModal;
(window as unknown as Record<string, unknown>).markModalDirty = markModalDirty;
